import { images } from '@/asset';
import { mapTypeToTitle } from '@/components/ExcludeRuleCondition';
import BaseImage from '@/components/Icons/BaseImage';
import RuleSummary from '@/components/RuleSummary';
import { ItemConditionEnum, LocationConditionEnum, Plan } from '@/constants/enum';
import { PATH } from '@/constants/path';
import { findLanguage } from '@/helpers';
import useConfirmationNavigate from '@/hooks/useConfirmationNavigate';
import { useHasWorkDay } from '@/hooks/useHasWorkDay';
import useUserPlan from '@/hooks/useUserPlan';
import { apiCaller } from '@/redux/query';
import { isSkipApiSelector } from '@/redux/slice/auth.slice';
import { isFetchingGeneralSettingsSelector } from '@/redux/slice/loading';
import previewSlice, { selectedLanguageSelector } from '@/redux/slice/preview.slice';
import ruleSlice from '@/redux/slice/rule.slice';
import settingsSlice, { dataSettingsSelector, modeLightSelector } from '@/redux/slice/settings.slice';
import { IRule } from '@/types/newSetup.type';
import {
  Button,
  Card,
  Divider,
  Icon,
  LegacyCard,
  Link,
  Select,
  SkeletonBodyText,
  SkeletonDisplayText,
  Text,
  TextContainer,
} from '@shopify/polaris';
import { AlertCircleIcon, LanguageTranslateIcon, MoonIcon, SunIcon } from '@shopify/polaris-icons';
import React, { memo, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import RegularText from '../RegularText';
import PreviewRule from './PreviewRule';
import { PreviewStyled } from './styled';
import { preloadImage } from '@/helpers/file';
interface IProps {
  children?: any;
  top: number;
  defaultData?: IRule;
  isInSmallPage?: boolean;
  zipcodePlaceholder?: string;
  isAutoDetect?: boolean;
  isShowRuleStatus?: boolean;
  checkScheduling?: boolean;
}

const Preview = ({
  children,
  top,
  defaultData,
  isInSmallPage,
  zipcodePlaceholder,
  isAutoDetect,
  isShowRuleStatus = true,
  checkScheduling,
}: IProps) => {
  const navigate = useConfirmationNavigate();
  const dispatch = useDispatch();
  const { currentPlan } = useUserPlan();
  const dataSettings = useSelector(dataSettingsSelector);
  const { isHasWorkDay } = useHasWorkDay();
  const isFetchingDataSettings = useSelector(isFetchingGeneralSettingsSelector);
  const modeLight = useSelector(modeLightSelector);

  const { data: allRules } = apiCaller.useGetRulesQuery({ page: 1, perPage: -1 });

  const isDisableActive = useMemo(() => {
    const isCurrentRuleActive = (allRules?.data || []).find((rule) => rule.id === defaultData?.id)?.enable;
    if (isCurrentRuleActive) return false;
    return !defaultData?.enable && currentPlan === Plan.FREE && (allRules?.data || []).filter((rule) => rule.enable).length > 0;
  }, [defaultData, currentPlan, allRules?.data]);

  const isSkip = useSelector(isSkipApiSelector);
  const selectedLanguage = useSelector(selectedLanguageSelector);

  const { data: languageRules, isLoading } = apiCaller.useGetLanguageRulesQuery({ page: 1, perPage: -1 }, { skip: isSkip });

  const languageOptions: { value: string; label: string }[] = useMemo(() => {
    const defaultLanguage = findLanguage(dataSettings.date_locale);
    const additionalLanguages = (languageRules?.data || [])
      .filter((item) => item.active && item.codeLanguage !== dataSettings.date_locale)
      .map((item) => ({
        value: item.codeLanguage,
        label: item.nameLanguage,
      }));
    return defaultLanguage ? [defaultLanguage, ...additionalLanguages] : additionalLanguages;
  }, [dataSettings.date_locale, languageRules?.data]);

  const currentLanguageRule = useMemo(() => {
    if (selectedLanguage === dataSettings.date_locale) return;
    return languageRules?.data?.find((item) => item.codeLanguage === selectedLanguage);
  }, [selectedLanguage, dataSettings.date_locale, languageRules?.data]);

  const handleChangeLanguage = (value: string) => {
    dispatch(previewSlice.actions.handleSetSelectedLanguage(value));
  };

  const handleStatus = () => {
    if (!defaultData) return;
    dispatch(ruleSlice.actions.handleChange({ key: 'enable', value: !defaultData.enable }));
  };

  const isEmptyCondition = useMemo(() => {
    return defaultData?.itemConditions?.length === 0 && defaultData?.locationConditions?.length === 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(defaultData)]);

  const locationConditions = useMemo(() => {
    const mapTitle = {
      [LocationConditionEnum.Country]: 'Locations, regions',
      [LocationConditionEnum.State]: 'States',
      [LocationConditionEnum.Zipcode]: 'Zipcodes',
      [LocationConditionEnum.City]: 'Cities',
    };
    return (
      defaultData?.locationConditions?.map(
        (item) =>
          `${mapTitle[item.type]} (${
            item.type !== LocationConditionEnum.Zipcode
              ? item.value?.filter((i) => i?.data)?.length
              : item.value?.[0]?.data?.split(',')?.filter((i) => !!i)?.length
          })`,
      ) || []
    );
  }, [defaultData?.locationConditions]);

  const itemConditions = useMemo(() => {
    const inventoryData = defaultData?.itemConditions?.find((item) => item.type === ItemConditionEnum.InventoryQuantity)
      ?.value?.[0];
    const inventoryText = `${inventoryData?.minInventoryQuantity ? 'Minimum: ' : ''}${
      inventoryData?.minInventoryQuantity ? inventoryData?.minInventoryQuantity : ''
    } ${inventoryData?.minInventoryQuantity && inventoryData?.maxInventoryQuantity ? '-' : ''} ${
      inventoryData?.maxInventoryQuantity ? 'Maximum: ' : ''
    }${inventoryData?.maxInventoryQuantity ? inventoryData?.maxInventoryQuantity : ''}`;

    return (
      defaultData?.itemConditions
        ?.filter((item) => item.type !== ItemConditionEnum.Exception)
        ?.map(
          (item) =>
            `${mapTypeToTitle[item.type]} (${
              item.type !== ItemConditionEnum.InventoryQuantity ? item.value?.filter((i) => i?.data)?.length : inventoryText
            })`,
        ) || []
    );
  }, [defaultData?.itemConditions]);

  const hasDataAdvance = useMemo(() => {
    return defaultData?.showTextInCart || defaultData?.soldOutEnabled;
  }, [defaultData?.showTextInCart, defaultData?.soldOutEnabled]);

  const textDataException: React.ReactNode = useMemo(() => {
    const dataException = defaultData?.itemConditions?.find((item) => item.type === ItemConditionEnum.Exception)?.value;
    return dataException ? (
      <>
        <Divider />
        <div className="mt-4">{`${mapTypeToTitle[ItemConditionEnum.Exception]} (${
          dataException?.filter((i) => i?.data)?.length
        })`}</div>
      </>
    ) : null;
  }, [defaultData?.itemConditions]);

  useEffect(() => {
    preloadImage(images.demo_product);
  }, []);

  return (
    <PreviewStyled top={top} generalSetting={dataSettings} isInSmallPage={isInSmallPage}>
      {isShowRuleStatus && defaultData ? (
        <RuleSummary
          defaultData={defaultData}
          isDisableActive={isDisableActive}
          handleStatus={handleStatus}
          isEmptyCondition={isEmptyCondition}
          locationConditions={locationConditions}
          itemConditions={itemConditions}
          textDataException={textDataException}
          hasDataAdvance={hasDataAdvance}
        />
      ) : null}
      <Card padding={'0'}>
        <div className="preview-header content-between">
          <div className="flex-center">
            <Text as="span" variant="bodyMd">
              PREVIEW
            </Text>
          </div>
          <div className="preview-button-group">
            <Select
              label={<Icon source={LanguageTranslateIcon} />}
              options={languageOptions}
              value={selectedLanguage}
              onChange={handleChangeLanguage}
              labelInline
              disabled={isLoading}
            />
            <Button
              icon={modeLight ? SunIcon : MoonIcon}
              variant="tertiary"
              removeUnderline
              onClick={() => dispatch(settingsSlice.actions.handleModeLight())}
            >
              {(<div className="mode-light">{modeLight ? 'Light' : 'Dark'}</div>) as any}
            </Button>
          </div>
        </div>
        <div className={`preview-content ${modeLight ? `light-mode` : `dark-mode`}`}>
          <div className="mb-8 d-flex gap-4">
            <div className="icon">
              <Icon source={AlertCircleIcon}></Icon>
            </div>
            <RegularText tone={modeLight ? 'subdued' : undefined}>
              Preview may not accurately reflect which rules are shown for products or locations. Please view on live store.
            </RegularText>
          </div>
          <div className="d-flex">
            <BaseImage class="preview-content-image-left" src={images.demo_product} alt="preview" />
            <div className="preview-content-image-right">PRODUCT DEMO</div>
          </div>
          <div className="mt-8 mb-16">
            <Button disabled={true} fullWidth>
              ADD TO CART
            </Button>
          </div>
          {isFetchingDataSettings ? (
            <div className="ot-estimated-shipping">
              <LegacyCard>
                <LegacyCard.Section>
                  <TextContainer>
                    <SkeletonDisplayText size="small" />
                    <SkeletonBodyText lines={2} />
                  </TextContainer>
                </LegacyCard.Section>
                <LegacyCard.Section>
                  <SkeletonBodyText lines={1} />
                </LegacyCard.Section>
              </LegacyCard>
            </div>
          ) : (
            <div className="ot-estimated-shipping">
              {children ??
                (isHasWorkDay ? (
                  <PreviewRule
                    defaultData={defaultData}
                    languageRule={currentLanguageRule}
                    zipcodePlaceholder={zipcodePlaceholder}
                    isAutoDetect={isAutoDetect}
                    checkScheduling={checkScheduling}
                  />
                ) : (
                  <RegularText>
                    You must enable at least one work day in{' '}
                    <Link
                      onClick={() => {
                        navigate(PATH.GENERAL_SETTINGS);
                      }}
                    >
                      Date and time page
                    </Link>{' '}
                    to see the preview
                  </RegularText>
                ))}
            </div>
          )}
        </div>
      </Card>
    </PreviewStyled>
  );
};

export default memo(Preview);
